import { FunctionComponent, ReactElement, useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import dayjs from 'dayjs';
import CompletedFor from '../../components/completed-for';
import { DialogueContext } from '../../dialogues.types';
import { MAPPED_TAX_TYPES } from '@/clients/clients.constants';
import IpsForm, { IpsFormRef } from './components/ips-form';
import {
	useRiskAssessmentIPS,
	useRiskAssessmentIPSPDF,
	useUpdateRiskAssessmentIPS,
} from '../risk-assessment.queries';
import { IpsData, IpsFormData } from '@/app/api/flows/ips.types';
import { downloadFile } from '@/shared/utils';
import {
	IPS_DATE_FORMAT,
	TAX_CONSIDERATION_DEFAULT_COPY,
	TAX_EXEMPTIONS_DEFAULT_COPY,
	LIQUIDITY_REQUIREMENTS_DETAILS_DEFAULT_COPY,
} from './ips.constants';

const RiskAssessmentIPSPage: FunctionComponent = (): ReactElement | null => {
	const { client } = useOutletContext<DialogueContext>();
	const [idPdfDownload, setIdPdfDownload] = useState<string | undefined>(undefined);
	const ipsFormRef = useRef<IpsFormRef>(null);
	const [defaultValues, setDefaultValues] = useState<IpsFormData & IpsData>({
		riskProfile: '',
		timeHorizon: '',
		taxConsideration: TAX_CONSIDERATION_DEFAULT_COPY,
		taxExemptions: TAX_EXEMPTIONS_DEFAULT_COPY,
		liquidityRequirementsDetails: LIQUIDITY_REQUIREMENTS_DETAILS_DEFAULT_COPY,
		ipsFooter: '',
		ipsLogo: undefined,
		riskProfileOptions: [],
		timeHorizonOptions: [],
	});

	const { data: ips } = useRiskAssessmentIPS(client.id);

	useEffect(() => {
		if (ips) {
			setDefaultValues({
				...defaultValues,
				...ips,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ips]);

	const { mutate: updateIPS, isPending: isUpdatingIPS } = useUpdateRiskAssessmentIPS();
	const { data: pdfData, isFetching: isDownloadingPDF } = useRiskAssessmentIPSPDF(
		client.id,
		idPdfDownload
	);

	useEffect(() => {
		if (pdfData) {
			const date = dayjs(new Date()).format(IPS_DATE_FORMAT);
			const filename = `${client.clientName}_${date}_IPS.pdf`;

			downloadFile(pdfData, filename);
			setIdPdfDownload(undefined);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pdfData]);

	const onSubmit = (ipsFormData: IpsFormData) => {
		const formData = new FormData();

		Object.entries(ipsFormData).forEach(([key, value]) => {
			formData.append(key, value as string);
		});

		updateIPS(
			{
				clientId: client.id,
				payload: formData,
			},
			{
				onSuccess: ({ pdfId }) => {
					setIdPdfDownload(pdfId);
					ipsFormRef.current?.resetFileState();
				},
				onError: () => {
					setIdPdfDownload(undefined);
					ipsFormRef.current?.fileRemoved();
				},
			}
		);
	};

	return (
		<>
			<CompletedFor completedFor={MAPPED_TAX_TYPES[client.taxType]} />

			{ips && (
				<IpsForm
					ref={ipsFormRef}
					onSubmit={onSubmit}
					defaultValues={defaultValues}
					submitDisabled={isUpdatingIPS || isDownloadingPDF}
				/>
			)}
		</>
	);
};

export default RiskAssessmentIPSPage;
